import React, { useState, useRef, useEffect, useMemo } from "react";
import { McapIndexedReader } from "@mcap/core";
import { loadDecompressHandlers } from "@mcap/support";
import { BlobReadable } from "@mcap/browser";
import protobuf from "protobufjs";
import { FileDescriptorSet } from "protobufjs/ext/descriptor";
import FileInfo from "./FileInfo";
import Timeline from "./Timeline";
import TopicsList from "./TopicsList";
import "./MCAPPlayer.css";

// Helper functions for message decoding
function bytesToHex(u8, maxBytes = 64) {
  if (!(u8 instanceof Uint8Array)) return "";
  const n = Math.min(u8.length, maxBytes);
  let s = "";
  for (let i = 0; i < n; i++) s += u8[i].toString(16).padStart(2, "0");
  return u8.length > maxBytes ? `${s}… (+${u8.length - maxBytes} bytes)` : s;
}

function safeBigIntToString(_k, v) {
  return typeof v === "bigint" ? v.toString() : v;
}

function decodeJsonPayload(dataU8) {
  try {
    const text = new TextDecoder("utf-8", { fatal: false }).decode(dataU8);
    return { kind: "json", value: JSON.parse(text) };
  } catch {
    return null;
  }
}

/**
 * Build a protobufjs Root from a binary FileDescriptorSet.
 * Assumes schema.data contains FileDescriptorSet bytes.
 */
function rootFromFileDescriptorSet(schemaDataU8) {
  const fds = FileDescriptorSet.decode(schemaDataU8);
  const root = protobuf.Root.fromDescriptor(fds);
  return root;
}

function decodeProtobufPayload({ dataU8, schema, cache }) {
  // Cache per schema.id to avoid rebuilding reflection repeatedly
  let entry = cache.get(schema.id);
  if (!entry) {
    const root = rootFromFileDescriptorSet(schema.data);
    // MCAP schema.name should be the fully qualified message name
    const type = root.lookupType(schema.name);
    entry = { root, type };
    cache.set(schema.id, entry);
  }

  const msg = entry.type.decode(dataU8);
  const obj = entry.type.toObject(msg, {
    longs: String,
    enums: String,
    bytes: String,
    defaults: false,
    oneofs: true,
  });
  return { kind: "protobuf", value: obj };
}

const MCAPPlayer = () => {
  const [file, setFile] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [topics, setTopics] = useState([]);
  const [messages, setMessages] = useState([]);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);

  const playbackIntervalRef = useRef(null);
  const fileInputRef = useRef(null);

  // Protobuf decode cache (schemaId -> {root, type})
  const protobufCache = useMemo(() => new Map(), [file]);

  const handleFileSelect = async (event) => {
    const selectedFile = event.target.files[0];

    if (!selectedFile) return;

    if (!selectedFile.name.endsWith(".mcap")) {
      setError("Please select a valid .mcap file");
      return;
    }

    setFile(selectedFile);
    setError(null);
    setLoading(true);
    setIsPlaying(false);

    try {
      await parseMCAPFile(selectedFile);
    } catch (err) {
      setError(`Failed to parse MCAP file: ${err.message}`);
      setFileInfo(null);
      setTopics([]);
      setMessages([]);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const parseMCAPFile = async (file) => {
    const decompressHandlers = await loadDecompressHandlers();
    const readable = new BlobReadable(file);

    const reader = await McapIndexedReader.Initialize({
      readable,
      decompressHandlers,
    });

    // Get file metadata
    const info = {
      profile: reader.header.profile,
      library: reader.header.library,
      startTime: reader.startTime,
      endTime: reader.endTime,
      duration: (Number(reader.endTime) - Number(reader.startTime)) / 1e9, // Convert to seconds
      messageCount: 0,
    };

    // Get all schemas and channels
    const channelsById = new Map();
    const schemasById = new Map();

    for (const [id, channel] of reader.channelsById) {
      channelsById.set(id, channel);
    }

    for (const [id, schema] of reader.schemasById) {
      schemasById.set(id, schema);
    }

    // Read all messages
    const allMessages = [];
    let messageCount = 0;

    for await (const msg of reader.readMessages()) {
      const ch = channelsById.get(msg.channelId);
      const schema =
        ch?.schemaId != null ? schemasById.get(ch.schemaId) : undefined;

      const msgEnc = (ch?.messageEncoding || "").toLowerCase();
      const schemaEnc = (schema?.encoding || "").toLowerCase();

      let decoded = null;
      let decodeError = null;

      try {
        if (msgEnc.includes("json")) {
          decoded = decodeJsonPayload(msg.data);
        } else if (
          msgEnc.includes("protobuf") &&
          schema &&
          schemaEnc === "protobuf"
        ) {
          decoded = decodeProtobufPayload({
            dataU8: msg.data,
            schema: { id: schema.id, name: schema.name, data: schema.data },
            cache: protobufCache,
          });
        } else {
          // best-effort JSON even when messageEncoding isn't explicit
          decoded = decodeJsonPayload(msg.data);
        }
      } catch (err) {
        decodeError = err?.message ?? String(err);
      }

      allMessages.push({
        channelId: msg.channelId,
        sequence: msg.sequence,
        logTime: msg.logTime,
        publishTime: msg.publishTime,
        data: msg.data,
        topic: ch?.topic ?? `(channel ${msg.channelId})`,
        messageEncoding: ch?.messageEncoding ?? "",
        schemaId: ch?.schemaId ?? null,
        schemaName: schema?.name ?? "",
        schemaEncoding: schema?.encoding ?? "",
        decoded,
        decodeError,
      });
      messageCount++;
    }

    info.messageCount = messageCount;

    // Group messages by topic
    const topicMap = new Map();

    for (const message of allMessages) {
      const channel = channelsById.get(message.channelId);
      if (channel) {
        const topicName = channel.topic;
        if (!topicMap.has(topicName)) {
          const schema = schemasById.get(channel.schemaId);
          topicMap.set(topicName, {
            topic: topicName,
            messageEncoding: channel.messageEncoding,
            schemaName: schema?.name || "unknown",
            messageCount: 0,
            channelId: message.channelId,
          });
        }
        topicMap.get(topicName).messageCount++;
      }
    }

    setFileInfo(info);
    setTopics(Array.from(topicMap.values()));
    setMessages(allMessages);
    setCurrentMessageIndex(0);
  };

  const handlePlayPause = () => {
    if (isPlaying) {
      stopPlayback();
    } else {
      startPlayback();
    }
  };

  const startPlayback = () => {
    if (!messages.length) return;

    setIsPlaying(true);

    playbackIntervalRef.current = setInterval(() => {
      setCurrentMessageIndex((prevIndex) => {
        if (prevIndex >= messages.length - 1) {
          stopPlayback();
          return prevIndex;
        }
        return prevIndex + 1;
      });
    }, 100 / playbackSpeed); // Base speed of 100ms per message
  };

  const stopPlayback = () => {
    setIsPlaying(false);
    if (playbackIntervalRef.current) {
      clearInterval(playbackIntervalRef.current);
      playbackIntervalRef.current = null;
    }
  };

  const handleSeek = (index) => {
    setCurrentMessageIndex(index);
  };

  const handleReset = () => {
    stopPlayback();
    setCurrentMessageIndex(0);
  };

  const getCurrentTime = () => {
    if (!messages.length || !fileInfo) return 0;
    const currentMessage = messages[currentMessageIndex];
    if (!currentMessage) return 0;
    return (Number(currentMessage.logTime) - Number(fileInfo.startTime)) / 1e9;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 1000);
    return `${mins}:${secs.toString().padStart(2, "0")}.${ms.toString().padStart(3, "0")}`;
  };

  useEffect(() => {
    return () => {
      stopPlayback();
    };
  }, []);

  return (
    <div className="mcap-player">
      <div className="mcap-player-header">
        <h1>MCAP File Player</h1>
        <p className="subtitle">
          Upload and play back MCAP (Message Capture) files
        </p>
      </div>

      <div className="file-selector-section">
        <input
          ref={fileInputRef}
          type="file"
          accept=".mcap"
          onChange={handleFileSelect}
          style={{ display: "none" }}
          id="mcap-file-input"
        />
        <label htmlFor="mcap-file-input" className="file-select-button">
          <i className="fas fa-folder-open"></i>
          Choose MCAP File
        </label>
        {file && <span className="selected-filename">{file.name}</span>}
      </div>

      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i>
          {error}
        </div>
      )}

      {loading && (
        <div className="loading-message">
          <div className="spinner"></div>
          <span>Parsing MCAP file...</span>
        </div>
      )}

      {fileInfo && !loading && (
        <>
          <FileInfo info={fileInfo} />

          <div className="playback-controls">
            <button
              onClick={handlePlayPause}
              className={`control-button play-pause ${isPlaying ? "playing" : ""}`}
              disabled={!messages.length}
            >
              <i className={`fas fa-${isPlaying ? "pause" : "play"}`}></i>
            </button>

            <button
              onClick={handleReset}
              className="control-button"
              disabled={!messages.length}
            >
              <i className="fas fa-redo"></i>
            </button>

            <div className="time-display">
              <span className="current-time">
                {formatTime(getCurrentTime())}
              </span>
              <span className="separator">/</span>
              <span className="total-time">
                {formatTime(fileInfo.duration)}
              </span>
            </div>

            <div className="speed-control">
              <label>Speed:</label>
              <select
                value={playbackSpeed}
                onChange={(e) => setPlaybackSpeed(Number(e.target.value))}
              >
                <option value="0.25">0.25x</option>
                <option value="0.5">0.5x</option>
                <option value="1">1x</option>
                <option value="2">2x</option>
                <option value="4">4x</option>
              </select>
            </div>

            <div className="message-counter">
              Message: {currentMessageIndex + 1} / {messages.length}
            </div>
          </div>

          <Timeline
            currentIndex={currentMessageIndex}
            totalMessages={messages.length}
            onSeek={handleSeek}
            duration={fileInfo.duration}
            messages={messages}
            startTime={fileInfo.startTime}
          />

          <TopicsList topics={topics} />
        </>
      )}

      {!file && !loading && !error && (
        <div className="empty-state">
          <i className="fas fa-file-upload"></i>
          <h3>No MCAP file selected</h3>
          <p>Choose an MCAP file to get started</p>
        </div>
      )}
    </div>
  );
};

export default MCAPPlayer;
